// src/components/AddTokenReminderPopup.jsx
import React, { useState } from 'react';

const AddTokenReminderPopup = ({ 
  isOpen, 
  onClose,
  tokenAddress,
  tokenSymbol = "YILDX",
  tokenDecimals = 18,
  tokenImage = "" // Optional token logo URL
}) => {
  // Track if token is currently being added
  const [isAddingToken, setIsAddingToken] = useState(false);
  // Track if token was successfully added
  const [tokenAdded, setTokenAdded] = useState(false);
  // Track errors
  const [error, setError] = useState(null);

  // Helper function to add token to MetaMask
  const addTokenToMetaMask = async () => {
    if (!window.ethereum) {
      setError("MetaMask is not installed. Please install MetaMask and try again.");
      return;
    }

    setIsAddingToken(true);
    setError(null);

    try {
      // Request to add token to MetaMask
      const wasAdded = await window.ethereum.request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC20',
          options: {
            address: tokenAddress,
            symbol: tokenSymbol,
            decimals: tokenDecimals,
            image: tokenImage || undefined, // Only include if provided
          },
        },
      });

      if (wasAdded) {
        setTokenAdded(true);
      } else {
        setError("Token addition was cancelled.");
      }
    } catch (error) {
      console.error("Error adding token to MetaMask:", error);
      setError(error.message || "Failed to add token to MetaMask.");
    } finally {
      setIsAddingToken(false);
    }
  };

  // Copy token address to clipboard
  const copyAddressToClipboard = () => {
    navigator.clipboard.writeText(tokenAddress)
      .then(() => {
        // Show temporary "Copied!" indicator
        const addressElement = document.getElementById('token-address');
        if (addressElement) {
          const originalText = addressElement.innerText;
          addressElement.innerText = "Copied!";
          setTimeout(() => {
            addressElement.innerText = originalText;
          }, 1500);
        }
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
      });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative border-l-4 border-indigo-500">
        <button 
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        <div className="flex flex-col items-center">
          {/* Icon */}
          <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 sm:mx-0 sm:h-10 sm:w-10 mb-4">
            <svg className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </div>
          
          <h2 className="text-xl font-bold text-indigo-700 mb-2">
            Add {tokenSymbol} Token to MetaMask
          </h2>
          
          <p className="text-gray-600 text-center mb-4">
            To easily track your {tokenSymbol} tokens, add them to your MetaMask wallet.
          </p>
          
          {/* Token Details */}
          <div className="bg-gray-50 p-4 rounded-lg w-full mb-4">
            <div className="flex justify-between mb-2">
              <span className="font-medium">Token:</span>
              <span className="font-medium">{tokenSymbol}</span>
            </div>
            
            <div className="flex flex-col mb-2">
              <div className="flex justify-between mb-1">
                <span className="font-medium">Address:</span>
                <button 
                  onClick={copyAddressToClipboard}
                  className="text-indigo-500 hover:text-indigo-700"
                  title="Copy to clipboard"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                    <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                  </svg>
                </button>
              </div>
              <div 
                id="token-address"
                className="text-sm font-mono bg-gray-100 p-2 rounded break-all"
              >
                {tokenAddress}
              </div>
            </div>
            
            <div className="flex justify-between mb-2">
              <span className="font-medium">Decimals:</span>
              <span className="font-mono">{tokenDecimals}</span>
            </div>
          </div>
          
          {/* Success message */}
          {tokenAdded && (
            <div className="bg-green-50 border-l-4 border-green-500 text-green-700 p-4 mb-4 w-full">
              <p className="font-medium">Success!</p>
              <p className="text-sm">The {tokenSymbol} token has been added to your MetaMask wallet.</p>
            </div>
          )}
          
          {/* Error message */}
          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-4 w-full">
              <p className="font-medium">Error</p>
              <p className="text-sm">{error}</p>
            </div>
          )}
          
          <div className="w-full flex flex-col space-y-3">
            <button
              onClick={addTokenToMetaMask}
              disabled={isAddingToken || tokenAdded}
              className={`w-full px-6 py-2 rounded-lg font-medium text-white bg-indigo-500 hover:bg-indigo-600 
                focus:ring-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 
                ${(isAddingToken || tokenAdded) ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isAddingToken ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Adding to MetaMask...
                </span>
              ) : tokenAdded ? (
                'Added to MetaMask ✓'
              ) : (
                'Add to MetaMask'
              )}
            </button>
            <button
              onClick={onClose}
              className="w-full px-6 py-2 rounded-lg font-medium text-indigo-700 bg-indigo-100 hover:bg-indigo-200 
                focus:ring-indigo-300 focus:outline-none focus:ring-2 focus:ring-offset-2"
            >
              Close
            </button>
          </div>
          
          <p className="text-xs text-gray-500 mt-4 text-center">
            You can always add this token later by manually entering the token address in MetaMask.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AddTokenReminderPopup;